/**
 * PLAY hub. BLUEPRINT section 19.
 *
 * Four live tiles and five that are switched off until there is a server to
 * talk to. The disabled ones are shown rather than hidden, because a player
 * deciding whether to invest in this game should be able to see where it is
 * going.
 */

import { Link } from 'react-router-dom';
import { ROUTES } from '@/app/routes';
import { UiIcon, type UiIconName } from '@/ui/icons/UiIcon';
import styles from './PlayPage.module.css';

interface Tile {
  title: string;
  blurb: string;
  icon: UiIconName;
  to?: string;
  /** Shown instead of the arrow when the tile is not yet available. */
  lockedNote?: string;
}

const LIVE: Tile[] = [
  {
    title: 'Play vs bot',
    blurb: 'Pick a deck, pick an opponent, pick a difficulty. The full rules, no time limit.',
    icon: 'play',
    to: ROUTES.playBot,
  },
  {
    title: 'Deck test',
    blurb: 'Your deck against a bot with the game log open, for checking how a build actually plays.',
    icon: 'deck',
    to: ROUTES.playDeckTest,
  },
  {
    title: 'Test lab',
    blurb: 'Control both sides, force any board state, inspect the engine while it runs.',
    icon: 'gear',
    to: ROUTES.playLab,
  },
  {
    title: 'Tutorial',
    blurb: 'A scripted first game that teaches Wallets, Gas, the Stack and combat.',
    icon: 'help',
    to: ROUTES.playTutorial,
  },
];

const LOCKED: Tile[] = [
  { title: 'Casual', blurb: 'Unranked matches against other operators.', icon: 'play', lockedNote: 'Coming with Mainnet' },
  { title: 'Ranked', blurb: 'Ladder play with a rating and seasonal rewards.', icon: 'shield', lockedNote: 'Coming with Mainnet' },
  { title: 'Private match', blurb: 'Share a code and play whoever you like.', icon: 'lock', lockedNote: 'Coming with Mainnet' },
  { title: 'Friend match', blurb: 'Challenge someone from your friend list.', icon: 'profile', lockedNote: 'Coming with Mainnet' },
  { title: 'Events', blurb: 'Limited formats, drafts and weekend brawls.', icon: 'store', lockedNote: 'Coming with Mainnet' },
];

export function PlayPage() {
  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1>Play</h1>
        <p className={styles.lede}>Two operators, forty cards each, twenty Integrity. Last node standing.</p>
      </header>

      <div className={styles.grid}>
        {LIVE.map((tile) => (
          <Link key={tile.title} to={tile.to ?? ROUTES.play} className={styles.tile} data-testid={`tile-${slug(tile.title)}`}>
            <span className={styles.tileIcon}>
              <UiIcon name={tile.icon} size={22} />
            </span>
            <span className={styles.tileTitle}>{tile.title}</span>
            <span className={styles.tileBlurb}>{tile.blurb}</span>
            <span className={styles.tileGo}>
              <UiIcon name="chevronRight" size={16} />
            </span>
          </Link>
        ))}
      </div>

      <h2 className={styles.sectionTitle}>Not open yet</h2>
      <div className={styles.grid}>
        {LOCKED.map((tile) => (
          <div key={tile.title} className={`${styles.tile} ${styles.tileLocked}`} aria-disabled="true">
            <span className={styles.tileIcon}>
              <UiIcon name={tile.icon} size={22} />
            </span>
            <span className={styles.tileTitle}>{tile.title}</span>
            <span className={styles.tileBlurb}>{tile.blurb}</span>
            <span className={styles.tileNote}>{tile.lockedNote}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function slug(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-');
}

export default PlayPage;
